import React, { useState, useEffect } from 'react';
import { RefreshCw, Download, X, CheckCircle } from 'lucide-react';

interface UpdateAvailableBannerProps {
  className?: string;
}

export function UpdateAvailableBanner({ className = '' }: UpdateAvailableBannerProps) {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);

  useEffect(() => {
    // Check for service worker updates
    const checkForUpdates = async () => {
      if ('serviceWorker' in navigator) {
        try {
          const registration = await navigator.serviceWorker.ready;
          setRegistration(registration);

          // Listen for waiting service worker
          if (registration.waiting) {
            setUpdateAvailable(true);
          }

          // Listen for new service worker installing
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  setUpdateAvailable(true);
                }
              });
            }
          });

          // Listen for controller change (new service worker activated)
          navigator.serviceWorker.addEventListener('controllerchange', () => {
            if (!updating) {
              window.location.reload();
            }
          });

          // Check for updates every 5 minutes when tab is visible
          const interval = setInterval(() => {
            if (!document.hidden) {
              registration.update();
            }
          }, 5 * 60 * 1000);

          return () => clearInterval(interval);
        } catch (error) {
          console.error('Error setting up service worker update detection:', error);
        }
      }
    };

    checkForUpdates();

    // Check for updates when page becomes visible
    const handleVisibilityChange = () => {
      if (!document.hidden && registration) {
        registration.update();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [updating, registration]);

  const handleUpdate = async () => {
    if (!registration || updating) return;

    try {
      setUpdating(true);
      setUpdateSuccess(false);

      // Clear all caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
        console.log('All caches cleared');
      }

      // Clear localStorage items related to app state (but keep user preferences)
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (
          key.includes('ajel_') ||
          key.includes('chat_thread_') ||
          key.includes('notification-banner-dismissed')
        )) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));

      // Clear sessionStorage
      sessionStorage.clear();

      // Skip waiting and activate new service worker
      if (registration.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      }

      // Show success state briefly
      setUpdateSuccess(true);
      setTimeout(() => {
        // Force reload to get the new version
        window.location.reload();
      }, 1000);

    } catch (error) {
      console.error('Error during update:', error);
      setUpdating(false);
      // Force reload anyway
      window.location.reload();
    }
  };

  const dismissUpdate = () => {
    setUpdateAvailable(false);
    // Remember dismissal for this session
    sessionStorage.setItem('update-dismissed', 'true');
  };

  // Don't show if dismissed this session
  if (sessionStorage.getItem('update-dismissed')) {
    return null;
  }

  if (!updateAvailable) {
    return null;
  }

  return (
    <div className={`fixed top-0 left-0 right-0 z-50 ${className}`}>
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-4 py-3 shadow-lg animate-fade-in">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              {updateSuccess ? (
                <CheckCircle className="w-6 h-6 text-green-300" />
              ) : (
                <Download className="w-6 h-6 animate-bounce" />
              )}
            </div>
            <div>
              <p className="font-medium">
                {updateSuccess ? 'تم التحديث بنجاح!' : 'إصدار جديد متاح'}
              </p>
              <p className="text-sm opacity-90">
                {updateSuccess 
                  ? 'جاري إعادة تحميل التطبيق...'
                  : 'احصل على أحدث الميزات والتحسينات'
                }
              </p>
            </div>
          </div>
          
          {!updateSuccess && (
            <div className="flex items-center space-x-2">
              <button
                onClick={handleUpdate}
                disabled={updating}
                className="flex items-center space-x-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updating ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                <span className="text-sm font-medium">
                  {updating ? 'جاري التحديث...' : 'تحديث الآن'}
                </span>
              </button>
              
              <button
                onClick={dismissUpdate}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                title="تجاهل"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}